import { useRef } from "react";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import CurrencyField from "./CurrencyField";
import { colors } from "../constants/designTokens";
import {
  DRE_COLUMNS,
  DRE_LINES,
  isEditableImportedColumn,
} from "../services/dreService";
import { formatCurrency, formatPercent } from "../utils/formatters";

const columnWidths = [260, 160, 100, 145, 145, 145, 145, 145, 100, 145, 100];
const TABLE_MIN_WIDTH = columnWidths.reduce((total, width) => total + width, 0);
const VIABILIDADE_LEFT = columnWidths[0];

const highlightColors = {
  emerald: "var(--ac-highlight-emerald, #e7fbf2)",
  blue: "var(--ac-highlight-blue, #eef6ff)",
  amber: "var(--ac-highlight-amber, #fff4df)",
  purple: "var(--ac-highlight-purple, #f4eaff)",
};

function DreColumnGroup() {
  return (
    <colgroup>
      {columnWidths.map((width, index) => (
        <col key={index} style={{ width, minWidth: width }} />
      ))}
    </colgroup>
  );
}

function DreTableHeader() {
  return (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{
            bgcolor: colors.surfaceAlt,
            color: colors.text,
            fontWeight: 800,
            borderColor: colors.border,
            minWidth: columnWidths[0],
            position: "sticky",
            left: 0,
            zIndex: 4,
          }}
        >
          Descrição
        </TableCell>
        {DRE_COLUMNS.map((column, index) => (
          <TableCell
            key={column.key}
            align="right"
            sx={{
              bgcolor: column.editable ? "var(--ac-editable-bg, #fff5c7)" : colors.surfaceAlt,
              color: colors.text,
              fontWeight: 800,
              borderColor: colors.border,
              minWidth: columnWidths[index + 1],
              whiteSpace: "nowrap",
              ...(column.key === "viabilidade"
                ? {
                    position: "sticky",
                    left: VIABILIDADE_LEFT,
                    zIndex: 4,
                    boxShadow: "5px 0 8px -6px rgba(15, 23, 42, 0.45)",
                  }
                : {}),
            }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function DreTable({ lineData, calculatedRows, onCellChange }) {
  const headerScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const syncingScrollRef = useRef(false);

  function syncHorizontalScroll(source, scrollLeft) {
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;

    [headerScrollRef.current, topScrollRef.current, bodyScrollRef.current]
      .filter((element) => element && element !== source)
      .forEach((element) => {
        element.scrollLeft = scrollLeft;
      });

    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  }

  function handleHorizontalScroll(event) {
    syncHorizontalScroll(event.currentTarget, event.currentTarget.scrollLeft);
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 2.4,
        border: `1px solid ${colors.border}`,
        boxShadow: "0 14px 32px rgba(24,39,75,0.06)",
        width: "100%",
        maxWidth: "100%",
        bgcolor: colors.surface,
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          width: "100%",
          bgcolor: colors.surfaceAlt,
          borderTopLeftRadius: 9,
          borderTopRightRadius: 9,
          boxShadow: "0 4px 10px rgba(24,39,75,0.08)",
        }}
      >
        <TableContainer
          ref={headerScrollRef}
          onScroll={handleHorizontalScroll}
          sx={{
            width: "100%",
            overflowX: "hidden",
            overflowY: "hidden",
          }}
        >
          <Table
            size="small"
            aria-hidden="true"
            sx={{
              minWidth: TABLE_MIN_WIDTH,
              tableLayout: "fixed",
            }}
          >
            <DreColumnGroup />
            <DreTableHeader />
          </Table>
        </TableContainer>

        <Box
          ref={topScrollRef}
          onScroll={handleHorizontalScroll}
          aria-label="Rolagem horizontal da DRE de Viabilidade"
          sx={{
            width: "100%",
            height: 18,
            minHeight: 18,
            flexShrink: 0,
            overflowX: "scroll",
            overflowY: "hidden",
            bgcolor: colors.surface,
            borderTop: `1px solid ${colors.border}`,
            borderBottom: `1px solid ${colors.border}`,
            scrollbarColor: `${colors.softMuted} ${colors.surfaceSoft}`,
            "&::-webkit-scrollbar": { height: 12 },
            "&::-webkit-scrollbar-track": { bgcolor: colors.surfaceSoft },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: colors.softMuted,
              borderRadius: 1,
              border: `2px solid ${colors.surfaceSoft}`,
            },
          }}
        >
          <Box sx={{ width: TABLE_MIN_WIDTH, height: 18 }} />
        </Box>
      </Box>

      <TableContainer
        ref={bodyScrollRef}
        onScroll={handleHorizontalScroll}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "visible",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table size="small" sx={{ minWidth: TABLE_MIN_WIDTH, tableLayout: "fixed" }}>
          <DreColumnGroup />
          <TableBody>
            {DRE_LINES.map((line, index) => {
              const row = calculatedRows[line.key] || {};
              const rowBackground =
                highlightColors[line.highlight] ||
                (index % 2 ? colors.surfaceAlt : colors.surface);

              return (
                <TableRow key={line.key} sx={{ bgcolor: rowBackground }}>
                  <TableCell
                    sx={{
                      color: colors.text,
                      fontWeight: line.bold ? 900 : 600,
                      pl: `${2 + line.indent * 2.2} !important`,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      bgcolor: rowBackground,
                    }}
                  >
                    {line.label}
                  </TableCell>

                  {DRE_COLUMNS.map((column) => {
                    if (
                      line.type === "input" &&
                      !line.readOnly &&
                      (
                        column.key === "viabilidade" ||
                        isEditableImportedColumn(line.key, column.key)
                      )
                    ) {
                      return (
                        <TableCell
                          key={column.key}
                          align="right"
                          sx={
                            column.key === "viabilidade"
                              ? {
                                  position: "sticky",
                                  left: VIABILIDADE_LEFT,
                                  zIndex: 2,
                                  bgcolor: rowBackground,
                                  boxShadow: "5px 0 8px -6px rgba(15, 23, 42, 0.45)",
                                }
                              : undefined
                          }
                        >
                          <CurrencyField
                            value={
                              column.key === "viabilidade"
                                ? lineData[line.key]?.viabilidade || 0
                                : Object.prototype.hasOwnProperty.call(
                                    lineData[line.key] || {},
                                    column.key
                                  )
                                ? lineData[line.key][column.key]
                                : row[column.key] || 0
                            }
                            onChange={(value) => onCellChange(line.key, column.key, value)}
                          />
                        </TableCell>
                      );
                    }

                    const value = row[column.key] ?? 0;
                    const isPercentColumn = ["pct_viab", "pct_tend", "desvio_pct"].includes(column.key);
                    const isDesvioColumn = ["desvio_rs", "desvio_pct"].includes(column.key);

                    let cellColor = colors.text;
                    if (isDesvioColumn && value > 0) {
                      cellColor = colors.red;
                    } else if (isDesvioColumn && value < 0) {
                      cellColor = colors.green;
                    } else if (!isPercentColumn && value < 0) {
                      cellColor = colors.red;
                    }

                    return (
                      <TableCell
                        key={column.key}
                        align="right"
                        sx={{
                          color: cellColor,
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          fontWeight: line.bold ? 800 : 500,
                          ...(column.key === "viabilidade"
                            ? {
                                position: "sticky",
                                left: VIABILIDADE_LEFT,
                                zIndex: 2,
                                bgcolor: rowBackground,
                                boxShadow: "5px 0 8px -6px rgba(15, 23, 42, 0.45)",
                              }
                            : {}),
                        }}
                      >
                        {isPercentColumn ? formatPercent(value, { decimals: 2 }) : formatCurrency(value)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
